/**
 * Right-hand panel: empty state or selected KPI with sub-metrics (horizontal bars).
 * Sub-metrics appear one by one with a staggered fade-in.
 * TRENDS button opens a modal with 12-month actual vs target chart.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import MDTypography from "components/MDTypography";
import { keyframes } from "@emotion/react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const subMetricFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Same as SBI Core QuattroRelatedComfort – icon per metric type
const METRIC_ICONS = {
  "Delirium / PND": "psychology",
  "High Anxiety": "sentiment_very_dissatisfied",
  "Long Fluid Fasting Time": "schedule",
  "High Thirst": "local_drink",
  "High Pain": "mood_bad",
  "Burst Suppression Time": "show_chart",
  "Throat/Airway discomfort": "airline_seat_flat",
  Shivering: "ac_unit",
  "High Stress": "sentiment_very_dissatisfied",
  "Nausea / Vomiting": "sick",
  "Reduced well-being": "mood",
  "Hypothermia avoided": "ac_unit",
};

/** Format minutes as "m:ss min" for Burst Suppression Time. */
function formatMinutesMmSs(minutes) {
  if (minutes == null || typeof minutes !== "number" || Number.isNaN(minutes)) return "0:00 min";
  const totalSeconds = Math.round(minutes * 60);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")} min`;
}

// Plain-language descriptions per sub-metric for the Details modal
const SUBMETRIC_DESCRIPTIONS = {
  "Delirium / PND":
    "A sudden change in brain function after surgery that can cause confusion, inattention, and altered behavior.",
  "Burst Suppression Time":
    "A period during anesthesia when brain activity becomes intermittently very quiet, reflecting very deep brain suppression.",
  "Nausea / Vomiting":
    "A feeling of sickness in the stomach that may lead to throwing up.",
  "High Thirst":
    "A strong uncomfortable need to drink fluids.",
  "Long Fluid Fasting Time":
    "A prolonged time without drinking fluids before or around surgery.",
  Shivering: "Involuntary shaking or trembling, often related to feeling cold after surgery.",
  "High Pain": "Very strong pain that significantly limits comfort or movement.",
  "Throat/Airway discomfort":
    "Irritation or soreness in the throat or breathing passages, often felt as scratchiness, hoarseness, or coughing.",
  "High Anxiety": "Strong worry or fear related to the medical procedure or recovery.",
  "High Stress": "A heightened feeling of strain or being overwhelmed during the perioperative experience.",
  "Reduced well-being":
    "A general sense of feeling unwell, uncomfortable, or not recovering smoothly.",
};

function AnimatedBar({ value, max, color, opacity = 1, trackBg }) {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const pct = max ? Math.min(100, (value / max) * 100) : 0;
    const t = setTimeout(() => setWidth(pct), 50);
    return () => clearTimeout(t);
  }, [value, max]);

  return (
    <Box
      sx={{
        position: "relative",
        height: 8,
        flex: 1,
        overflow: "hidden",
        borderRadius: "9999px",
        bgcolor: trackBg ?? "rgba(255,255,255,0.1)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          width: `${width}%`,
          borderRadius: "9999px",
          background: color,
          opacity,
          transition: "width 500ms ease-out",
        }}
      />
    </Box>
  );
}

function DeltaBadge({ diff, size = "sm", deltaPositive, deltaNegative }) {
  const sign = diff >= 0 ? "+" : "";
  const pos = deltaPositive ?? { bg: "rgba(45, 212, 191, 0.15)", color: "#5eead4" };
  const neg = deltaNegative ?? { bg: "rgba(248, 113, 113, 0.2)", color: "#f87171" };
  return (
    <Box
      component="span"
      sx={{
        px: size === "sm" ? 1.5 : 0.75,
        py: 0.25,
        borderRadius: 1,
        fontSize: size === "sm" ? "0.75rem" : "10px",
        fontWeight: 600,
        fontFamily: "monospace",
        ...(diff >= 0 ? { bgcolor: pos.bg, color: pos.color } : { bgcolor: neg.bg, color: neg.color }),
      }}
    >
      {sign}
      {diff.toFixed(1)}pp
    </Box>
  );
}

const DEFAULT_TREND_BUTTON_COLOR = "#fb8c00";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Format minutes for chart axis (compact: "0:21", "11:52").
 */
function formatMinutesMmSsAxis(minutes) {
  if (minutes == null || typeof minutes !== "number" || Number.isNaN(minutes)) return "0:00";
  const totalSeconds = Math.round(minutes * 60);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Generate trend data: last 12 months, oldest (week11) on the left, latest (week0) on the right.
 * If weeklyPatientComfort is provided (array of 12 week patientComfort arrays), use those default values.
 * Otherwise use synthetic data derived from current subMetric values.
 */
function getTrendsData(subMetric, labelA, labelB, weeklyPatientComfort, benchmarkMode) {
  const isBurst = subMetric?.label === "Burst Suppression Time";
  const useDefaults = Array.isArray(weeklyPatientComfort) && weeklyPatientComfort.length === 12;
  const now = new Date();
  const data = [];

  if (useDefaults) {
    for (let i = 0; i < 12; i++) {
      const weekIndex = 11 - i;
      const weekData = weeklyPatientComfort[weekIndex] ?? [];
      const item = weekData.find((c) => c.title === subMetric?.label);
      const actual = item != null ? item.meValue : 0;
      const target =
        item != null
          ? benchmarkMode === "regional"
            ? item.benchmarkRegional
            : item.benchmarkOverall
          : isBurst ? 0.5 : 20;
      const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
      const monthLabel = `${MONTH_NAMES[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
      data.push({ month: monthLabel, [labelA]: actual, [labelB]: target });
    }
    return data;
  }

  const actualNow = subMetric?.variationA ?? 0;
  const targetNow = subMetric?.variationB ?? (isBurst ? 0.5 : 20);
  const seed = (subMetric?.label ?? "").split("").reduce((s, c) => s + c.charCodeAt(0), 0);
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
    const monthLabel = `${MONTH_NAMES[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
    const t = i / 11;
    const jitter = ((seed * (i + 1) * 9301 + 49297) % 233280) / 233280 - 0.5;
    const targetJitter = ((seed * (i + 2) * 9301 + 49297) % 233280) / 233280 - 0.5;

    if (isBurst) {
      const actual = Math.round(Math.max(0, actualNow * (0.2 + 0.8 * t) + jitter * actualNow * 0.15) * 100) / 100;
      const target = Math.round(Math.max(0, targetNow * (0.25 + 0.75 * t) + targetJitter * targetNow * 0.12) * 100) / 100;
      data.push({ month: monthLabel, [labelA]: actual, [labelB]: target });
    } else {
      const actual = Math.round(Math.max(0, Math.min(100, actualNow - (1 - t) * 15 + jitter * 8)) * 10) / 10;
      const targetTrend = (t - 0.5) * 6;
      const target = Math.round(Math.max(0, Math.min(100, targetNow + targetTrend + targetJitter * 6)) * 10) / 10;
      data.push({ month: monthLabel, [labelA]: actual, [labelB]: target });
    }
  }
  return data;
}

export default function SubMetricsPanel({
  kpi,
  labelA = "Me",
  labelB = "Benchmark",
  meColor = "#35c79a",
  benchmarkColor = "#f0825c",
  trendButtonColor = DEFAULT_TREND_BUTTON_COLOR,
  fillHeight = false,
  panelBg = "#252b30",
  palette,
  weeklyPatientComfort,
  benchmarkMode = "overall",
}) {
  const { t } = useTranslation();
  const [trendsModalOpen, setTrendsModalOpen] = useState(false);
  const [trendsModalSub, setTrendsModalSub] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsModalSub, setDetailsModalSub] = useState(null);

  const isLight = Boolean(palette);
  const textPrimary = isLight ? (palette.textPrimary ?? "#1a3d40") : "#fff";
  const textSecondary = isLight ? (palette.textSecondary ?? "#5a7a7d") : "rgba(255,255,255,0.7)";
  const panelBorder = isLight ? (palette.panelBorder ?? "rgba(0,0,0,0.08)") : "rgba(255,255,255,0.1)";
  const barTrackBg = isLight ? (palette.barTrackBg ?? "rgba(0,0,0,0.06)") : "rgba(255,255,255,0.1)";
  const deltaPositive = isLight ? (palette.deltaPositive ?? { bg: "rgba(45, 212, 191, 0.15)", color: "#0f766e" }) : { bg: "rgba(45, 212, 191, 0.15)", color: "#5eead4" };
  const deltaNegative = isLight ? (palette.deltaNegative ?? { bg: "rgba(248, 113, 113, 0.15)", color: "#dc2626" }) : { bg: "rgba(248, 113, 113, 0.2)", color: "#f87171" };
  const emptyBorder = isLight ? "rgba(0,0,0,0.12)" : "rgba(255,255,255,0.2)";
  const emptyIconBg = isLight ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)";
  const emptyIconStroke = isLight ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.5)";
  const cardHoverBg = isLight ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.06)";
  const cardBorder = isLight ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.1)";
  const detailsBtnBg = isLight ? "rgba(0,0,0,0.08)" : "rgba(0,0,0,0.45)";
  const subMetricCardBg = isLight ? (palette.subMetricCardBg ?? "#ffffff") : "rgba(255,255,255,0.12)";
  const subMetricCardHoverBg = isLight ? (palette.subMetricCardHoverBg ?? "rgba(0,0,0,0.03)") : "rgba(255,255,255,0.18)";

  const sectionTitleSx = {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: textSecondary,
    mb: 1.5,
  };

  if (!kpi) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 280,
          borderRadius: 2,
          border: `1px dashed ${emptyBorder}`,
          bgcolor: panelBg,
          p: 4,
          ...(fillHeight && { flex: 1, minHeight: 0, height: "100%" }),
        }}
      >
        <MDTypography variant="subtitle2" sx={sectionTitleSx}>
          {t("dashboard.patientsReportedComfort")}
        </MDTypography>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: emptyIconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1.5,
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke={emptyIconStroke}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 3v18h18" />
            <path d="m19 9-5 5-4-4-3 3" />
          </svg>
        </Box>
        <MDTypography
          variant="body2"
          sx={{ color: textSecondary, textAlign: "center" }}
        >
          Select a KPI on the left to view its sub-metrics breakdown
        </MDTypography>
        </Box>
      </Box>
    );
  }

  const diff = kpi.variationA - kpi.variationB;
  // Percentage bars use 0–100 scale so bar length matches the displayed % value
  const percentageBarMax = 100;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        border: `1px solid ${panelBorder}`,
        bgcolor: panelBg,
        overflow: "hidden",
        ...(fillHeight && { flex: 1, minHeight: 0, height: "100%" }),
      }}
    >
      <Box sx={{ borderBottom: `1px solid ${panelBorder}`, px: 3, pt: 2, pb: 1.5 }}>
        <MDTypography variant="subtitle2" sx={sectionTitleSx}>
          {t("dashboard.patientsReportedComfort")}
        </MDTypography>
      </Box>
      <Box sx={{ borderBottom: `1px solid ${panelBorder}`, px: 3, py: 2.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: meColor,
                }}
              />
              <MDTypography variant="subtitle2" sx={{ color: textPrimary, fontWeight: 600 }}>
                {kpi.label}
              </MDTypography>
            </Box>
            <MDTypography variant="caption" sx={{ color: textSecondary, ml: 2.5, display: "block" }}>
              {kpi.subMetrics?.length ?? 0} sub-metrics
            </MDTypography>
          </Box>
          <DeltaBadge diff={diff} deltaPositive={deltaPositive} deltaNegative={deltaNegative} />
        </Box>
        <Box mt={2} display="flex" alignItems="baseline" gap={1.5}>
          <Box display="flex" alignItems="center" gap={0.75}>
            <Box
              sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: meColor }}
            />
            <MDTypography variant="caption" sx={{ color: textSecondary }}>
              {labelA}
            </MDTypography>
            <MDTypography variant="body2" sx={{ fontWeight: 700, color: textPrimary, fontFamily: "monospace" }}>
              {kpi.variationA.toFixed(1)}%
            </MDTypography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.75}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: benchmarkColor,
              }}
            />
            <MDTypography variant="caption" sx={{ color: textSecondary }}>
              {labelB}
            </MDTypography>
            <MDTypography variant="body2" sx={{ fontWeight: 600, color: textSecondary, fontFamily: "monospace" }}>
              {kpi.variationB.toFixed(1)}%
            </MDTypography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>
        <Box display="flex" flexDirection="column" gap={2.5}>
          {(kpi.subMetrics || []).map((sub, index) => {
            const isBurstSuppression = sub.label === "Burst Suppression Time";
            const subDiff = (sub.variationA ?? 0) - (sub.variationB ?? 0);
            const fadeDelayMs = 120;
            const fadeDurationMs = 350;
            return (
              <Box
                key={sub.label}
                sx={{
                  borderRadius: 1.5,
                  border: `1px solid ${cardBorder}`,
                  bgcolor: subMetricCardBg,
                  p: 1.5,
                  transition: "background 0.2s",
                  "&:hover": { bgcolor: subMetricCardHoverBg },
                  opacity: 0,
                  animation: `${subMetricFadeIn} ${fadeDurationMs}ms ease-out forwards`,
                  animationDelay: `${index * fadeDelayMs}ms`,
                }}
              >
                <Box mb={1} display="flex" alignItems="center" justifyContent="space-between">
                  <MDTypography variant="body2" fontWeight={500} sx={{ color: textPrimary }}>
                    {sub.label}
                  </MDTypography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Icon sx={{ color: textSecondary, fontSize: "1.25rem" }}>
                      {METRIC_ICONS[sub.label] || "radio_button_unchecked"}
                    </Icon>
                    {!isBurstSuppression && (
                      <DeltaBadge diff={subDiff} size="xs" deltaPositive={deltaPositive} deltaNegative={deltaNegative} />
                    )}
                  </Box>
                </Box>
                {isBurstSuppression ? (
                  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <MDTypography variant="body2" sx={{ fontWeight: 600, color: meColor, fontFamily: "monospace" }}>
                      {formatMinutesMmSs(sub.variationA)}
                    </MDTypography>
                    <MDTypography variant="body2" sx={{ fontWeight: 600, color: benchmarkColor, fontFamily: "monospace" }}>
                      {formatMinutesMmSs(sub.variationB)}
                    </MDTypography>
                  </Box>
                ) : (
                <Box display="flex" flexDirection="column" gap={1}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Box sx={{ width: 56, fontSize: "11px", color: textSecondary }}>
                      {labelA}
                    </Box>
                    <AnimatedBar
                      value={sub.variationA ?? 0}
                      max={percentageBarMax}
                      color={meColor}
                      trackBg={barTrackBg}
                    />
                    <Box sx={{ width: 48, textAlign: "right", fontSize: "0.75rem", fontWeight: 600, color: textPrimary, fontFamily: "monospace" }}>
                      {(sub.variationA ?? 0).toFixed(1)}%
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Box sx={{ width: 56, fontSize: "11px", color: textSecondary }}>
                      {labelB}
                    </Box>
                    <AnimatedBar
                      value={sub.variationB ?? 0}
                      max={percentageBarMax}
                      color={benchmarkColor}
                      opacity={0.85}
                      trackBg={barTrackBg}
                    />
                    <Box sx={{ width: 48, textAlign: "right", fontSize: "0.75rem", color: textSecondary, fontFamily: "monospace" }}>
                      {(sub.variationB ?? 0).toFixed(1)}%
                    </Box>
                  </Box>
                </Box>
                )}
                <Box display="flex" gap={1} alignItems="center" mt={1.5}>
                  <Button
                    variant="contained"
                    size="small"
                    disableElevation
                    onClick={(event) => {
                      event.currentTarget.blur();
                      setTrendsModalSub(sub);
                      setTrendsModalOpen(true);
                    }}
                    sx={{
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      backgroundColor: trendButtonColor,
                      color: "#fff",
                      "&:hover": { backgroundColor: trendButtonColor, opacity: 0.9, color: "#fff" },
                    }}
                  >
                    {t("dashboard.trends")}
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    onClick={(event) => {
                      // Prevent the DETAILS button from staying focused after closing the modal
                      event.currentTarget.blur();
                      setDetailsModalSub(sub);
                      setDetailsModalOpen(true);
                    }}
                    sx={{
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: textPrimary,
                      minWidth: 0,
                      backgroundColor: detailsBtnBg,
                      "&:hover": {
                        backgroundColor: isLight ? "rgba(0,0,0,0.12)" : "rgba(0,0,0,0.55)",
                        color: textPrimary,
                      },
                    }}
                  >
                    {t("dashboard.details")}
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Trends modal (chart) */}
      <Dialog
        open={trendsModalOpen}
        onClose={() => setTrendsModalOpen(false)}
        disableRestoreFocus
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: panelBg,
            border: `1px solid ${panelBorder}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "#fff",
            borderBottom: `1px solid ${panelBorder}`,
            py: 1.5,
            px: 3,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: meColor }} />
            <MDTypography variant="h6" fontWeight={600} sx={{ color: "#fff" }}>
              {trendsModalSub?.label ?? "Trends"}
            </MDTypography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setTrendsModalOpen(false)}
            sx={{ color: textSecondary }}
            aria-label="Close"
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2, color: textPrimary }}>
          {(() => {
            const trendsData = trendsModalSub ? getTrendsData(trendsModalSub, labelA, labelB, weeklyPatientComfort, benchmarkMode) : [];
            const isBurstTrends = trendsModalSub?.label === "Burst Suppression Time";
            const yDomain = isBurstTrends ? [0, "auto"] : [0, 100];
            const yTickFormatter = isBurstTrends ? (v) => `${formatMinutesMmSsAxis(v)}` : (v) => `${v}%`;
            const tooltipFormatter = isBurstTrends
              ? (value, name) => [formatMinutesMmSs(Number(value)), name]
              : (value, name) => [`${Number(value).toFixed(1)}%`, name];
            const caption = isBurstTrends
              ? `Monthly average — ${labelA} vs ${labelB} (last 12 months, in min)`
              : `Monthly average — ${labelA} vs ${labelB} (last 12 months)`;
            return (
              <>
          <MDTypography variant="caption" sx={{ color: textSecondary, display: "block", mt: 2.5, mb: 2 }}>
            {caption}
          </MDTypography>
          <Box sx={{ width: "100%", height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={trendsData}
                margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={isLight ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.1)"} />
                <XAxis
                  dataKey="month"
                  interval={0}
                  tick={{ fontSize: 11, fill: textSecondary }}
                  stroke={textSecondary}
                  axisLine={{ stroke: panelBorder }}
                  tickLine={{ stroke: panelBorder }}
                />
                <YAxis
                  domain={yDomain}
                  tick={{ fontSize: 11, fill: textSecondary }}
                  stroke={textSecondary}
                  axisLine={{ stroke: panelBorder }}
                  tickLine={{ stroke: panelBorder }}
                  tickFormatter={yTickFormatter}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: panelBg,
                    border: `1px solid ${panelBorder}`,
                    borderRadius: 8,
                    color: textPrimary,
                  }}
                  labelStyle={{ color: textSecondary }}
                  formatter={tooltipFormatter}
                  labelFormatter={(label) => label}
                />
                <Legend
                  wrapperStyle={{ paddingTop: 8 }}
                  formatter={(entry) => entry}
                  iconType="line"
                  iconSize={10}
                  style={{ color: textPrimary, fontSize: "0.75rem" }}
                />
                <Line
                  type="monotone"
                  dataKey={labelA}
                  stroke={meColor}
                  strokeWidth={2}
                  dot={{ fill: meColor, r: 3 }}
                  activeDot={{ r: 5, strokeWidth: 0 }}
                  name={labelA}
                />
                <Line
                  type="monotone"
                  dataKey={labelB}
                  stroke={benchmarkColor}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  strokeOpacity={0.85}
                  dot={{ fill: benchmarkColor, r: 3, opacity: 0.85 }}
                  activeDot={{ r: 5, strokeWidth: 0 }}
                  name={labelB}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Details modal (plain-language description only) */}
      <Dialog
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        disableRestoreFocus
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: panelBg,
            border: `1px solid ${panelBorder}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${panelBorder}`,
            py: 1.5,
            px: 3,
          }}
        >
          <MDTypography variant="h6" fontWeight={600} sx={{ color: textPrimary }}>
            {detailsModalSub?.label ?? "Details"}
          </MDTypography>
          <IconButton
            size="small"
            onClick={() => setDetailsModalOpen(false)}
            sx={{ color: textSecondary }}
            aria-label="Close details"
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2, color: textPrimary }}>
          <MDTypography variant="body2" sx={{ color: textPrimary }}>
            {SUBMETRIC_DESCRIPTIONS[detailsModalSub?.label] ??
              "A detailed description for this sub-metric will be provided here."}
          </MDTypography>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
